import { db } from '../db/index.js';
import { getStore, updateStore, deleteStore } from '../db/stores.js';
import {
  getAislesByStore,
  addAisle,
  updateAisle,
  deleteAisle,
  reorderAisles,
  setAisleServerId,
} from '../db/aisles.js';
import { renderHeader } from '../components/header.js';
import { openModal, closeModal } from '../components/modal.js';
import { createElement, clearElement, showToast } from '../utils/dom.js';
import { navigate } from '../router.js';
import { iconTrash, iconGripVertical, iconPencil, iconCheck, iconChevronRight } from '../utils/icons.js';
import { pushCreateAisle, pushUpdateAisle, pushDeleteAisle, pushUpdateStore, pushDeleteStore } from '../api/user-sync.js';
import { requireOnline } from '../utils/network.js';

/**
 * Affiche la page de paramètres d'un magasin.
 * @param {HTMLElement} container
 * @param {number} storeId
 */
export async function renderStoreSettings(container, storeId) {
  clearElement(container);

  try {
    const store = await getStore(storeId);
    if (!store) {
      showToast('Magasin introuvable', 3000, 'error');
      navigate('/');
      return;
    }

    const role = store.role || 'owner';
    const isEditor = role === 'owner' || role === 'editor';
    const isOwner = role === 'owner';

    const aisles = await getAislesByStore(storeId);
    const refresh = () => renderStoreSettings(container, storeId);

    const page = createElement('div', { className: 'page page-settings' });

    const headerActions = [];
    if (isOwner) {
      headerActions.push({
        icon: iconTrash(20),
        label: 'Supprimer le magasin',
        onClick: () => confirmDeleteStore(store.name, storeId),
      });
    }

    page.appendChild(renderHeader({
      title: 'Paramètres',
      backPath: `/store/${storeId}`,
      actions: headerActions,
    }));

    // Section nom du magasin
    if (isEditor) {
      const nameInput = createElement('input', {
        className: 'input',
        attrs: { type: 'text', value: store.name },
      });

      const saveNameBtn = createElement('button', {
        className: 'btn btn--primary',
        attrs: { type: 'button' },
        text: 'Sauvegarder',
        events: {
          click: async () => {
            if (!requireOnline()) return;
            try {
              await updateStore(storeId, nameInput.value);
              const updatedStore = await getStore(storeId);
              pushUpdateStore(updatedStore?.serverId, nameInput.value, updatedStore?.order ?? 0);
              showToast('Nom mis à jour', 2000, 'success');
              refresh();
            } catch (err) {
              showToast(err.message || 'Erreur', 3000, 'error');
            }
          },
        },
      });

      page.appendChild(createElement('section', {
        className: 'page__section',
        children: [
          createElement('h2', { className: 'page__section-title', text: 'Nom du magasin' }),
          createElement('div', {
            className: 'page-settings__info',
            children: [
              createElement('div', {
                className: 'input-group',
                attrs: { style: 'margin-bottom: var(--space-sm)' },
                children: [nameInput],
              }),
              saveNameBtn,
            ],
          }),
        ],
      }));
    } else {
      page.appendChild(createElement('section', {
        className: 'page__section',
        children: [
          createElement('h2', { className: 'page__section-title', text: 'Magasin' }),
          createElement('div', {
            className: 'page-settings__info',
            children: [
              createElement('div', { className: 'page-settings__store-name', text: store.name }),
              createElement('div', {
                className: 'page-settings__store-meta text-muted',
                text: role === 'viewer' ? 'Vous êtes lecteur de cette liste' : 'Vous êtes éditeur de cette liste',
              }),
            ],
          }),
        ],
      }));
    }

    // Section rayons (éditeurs uniquement)
    if (isEditor) {
      const aisleList = createElement('div', { className: 'page-settings__aisle-list' });

      function renderAisleList(aisleData) {
        clearElement(aisleList);

        if (aisleData.length === 0) {
          aisleList.appendChild(createElement('p', {
            className: 'text-muted text-center',
            text: 'Aucun rayon. Ajoutez-en un ci-dessous.',
          }));
          return;
        }

        async function saveOrder() {
          if (!requireOnline()) return;
          const items = aisleList.querySelectorAll('.page-settings__aisle-item');
          const ids = Array.from(items).map((el) => Number(el.dataset.id));
          try {
            await reorderAisles(storeId, ids);
            refresh();
          } catch (err) {
            showToast(err.message || 'Erreur', 3000, 'error');
          }
        }

        function startRename(aisle, nameEl, editBtn) {
          const input = createElement('input', {
            className: 'input input--inline',
            attrs: { type: 'text', value: aisle.name },
          });
          const saveBtn = createElement('button', {
            className: 'btn btn--icon btn--icon-success',
            attrs: { type: 'button', 'aria-label': 'Valider' },
            children: [iconCheck(16)],
          });

          async function doRename() {
            if (!requireOnline()) return;
            const newName = input.value.trim();
            if (!newName || newName === aisle.name) {
              nameEl.textContent = aisle.name;
              nameEl.style.display = '';
              editBtn.style.display = '';
              input.remove();
              saveBtn.remove();
              return;
            }
            try {
              await updateAisle(aisle.id, { name: newName });
              pushUpdateAisle(aisle.serverId, newName, aisle.order);
              showToast('Rayon renommé', 2000, 'success');
              refresh();
            } catch (err) {
              showToast(err.message || 'Erreur', 3000, 'error');
            }
          }

          saveBtn.addEventListener('click', doRename);
          input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') doRename();
            if (e.key === 'Escape') {
              nameEl.textContent = aisle.name;
              nameEl.style.display = '';
              editBtn.style.display = '';
              input.remove();
              saveBtn.remove();
            }
          });

          nameEl.style.display = 'none';
          editBtn.style.display = 'none';
          nameEl.parentNode.insertBefore(input, nameEl);
          nameEl.parentNode.insertBefore(saveBtn, nameEl);
          input.focus();
          input.select();
        }

        // Drag & drop tactile
        let draggedEl = null;
        let dragPlaceholder = null;
        let startY = 0;
        let startTop = 0;

        function onTouchStart(e) {
          const grip = e.target.closest('.page-settings__drag-handle');
          if (!grip) return;
          draggedEl = grip.closest('.page-settings__aisle-item');
          if (!draggedEl) return;
          const rect = draggedEl.getBoundingClientRect();
          startY = e.touches[0].clientY;
          startTop = rect.top;
          dragPlaceholder = createElement('div', { className: 'page-settings__aisle-placeholder' });
          dragPlaceholder.style.height = rect.height + 'px';
          draggedEl.classList.add('page-settings__aisle-item--dragging');
          draggedEl.style.width = rect.width + 'px';
          draggedEl.style.position = 'fixed';
          draggedEl.style.top = rect.top + 'px';
          draggedEl.style.left = rect.left + 'px';
          draggedEl.style.zIndex = '1000';
          draggedEl.parentNode.insertBefore(dragPlaceholder, draggedEl);
          e.preventDefault();
        }

        function onTouchMove(e) {
          if (!draggedEl) return;
          e.preventDefault();
          const currentY = e.touches[0].clientY;
          draggedEl.style.top = (startTop + (currentY - startY)) + 'px';
          const siblings = Array.from(aisleList.querySelectorAll('.page-settings__aisle-item:not(.page-settings__aisle-item--dragging)'));
          for (const sibling of siblings) {
            const sibRect = sibling.getBoundingClientRect();
            if (currentY < sibRect.top + sibRect.height / 2) {
              aisleList.insertBefore(dragPlaceholder, sibling);
              return;
            }
          }
          aisleList.appendChild(dragPlaceholder);
        }

        function onTouchEnd() {
          if (!draggedEl) return;
          draggedEl.classList.remove('page-settings__aisle-item--dragging');
          draggedEl.style.position = '';
          draggedEl.style.top = '';
          draggedEl.style.left = '';
          draggedEl.style.width = '';
          draggedEl.style.zIndex = '';
          if (dragPlaceholder?.parentNode) {
            dragPlaceholder.parentNode.insertBefore(draggedEl, dragPlaceholder);
            dragPlaceholder.remove();
          }
          draggedEl = null;
          dragPlaceholder = null;
          const items = aisleList.querySelectorAll('.page-settings__aisle-item');
          items.forEach((el, i) => {
            const orderSpan = el.querySelector('.page-settings__aisle-order');
            if (orderSpan) orderSpan.textContent = String(i + 1);
          });
          saveOrder();
        }

        aisleList.addEventListener('touchstart', onTouchStart, { passive: false });
        aisleList.addEventListener('touchmove', onTouchMove, { passive: false });
        aisleList.addEventListener('touchend', onTouchEnd);

        aisleData.forEach((aisle, index) => {
          const item = createElement('div', {
            className: 'page-settings__aisle-item sortable-list__item',
            attrs: { 'data-id': String(aisle.id) },
          });
          const dragHandle = createElement('div', {
            className: 'page-settings__drag-handle',
            attrs: { 'aria-label': 'Maintenir pour déplacer' },
            children: [iconGripVertical(18)],
          });
          const orderEl = createElement('span', {
            className: 'page-settings__aisle-order',
            text: String(index + 1),
          });
          const nameEl = createElement('span', {
            className: 'page-settings__aisle-name',
            text: aisle.name,
          });
          const editBtn = createElement('button', {
            className: 'btn btn--icon btn--icon-muted',
            attrs: { type: 'button', 'aria-label': 'Renommer le rayon' },
            children: [iconPencil(16)],
            events: { click: () => startRename(aisle, nameEl, editBtn) },
          });
          const deleteBtn = createElement('button', {
            className: 'btn btn--icon btn--icon-danger',
            attrs: { type: 'button', 'aria-label': 'Supprimer le rayon' },
            children: [iconTrash(18)],
            events: {
              click: async () => {
                if (!requireOnline()) return;
                if (!confirm(`Supprimer le rayon « ${aisle.name} » ?`)) return;
                try {
                  await deleteAisle(aisle.id);
                  pushDeleteAisle(aisle.serverId);
                  showToast('Rayon supprimé', 2000, 'success');
                  refresh();
                } catch (err) {
                  showToast(err.message || 'Erreur', 3000, 'error');
                }
              },
            },
          });
          item.append(dragHandle, orderEl, nameEl, editBtn, deleteBtn);
          aisleList.appendChild(item);
        });
      }

      renderAisleList(aisles);

      const newAisleInput = createElement('input', {
        className: 'input',
        attrs: { type: 'text', placeholder: 'Nom du rayon' },
      });

      const addAisleBtn = createElement('button', {
        className: 'btn btn--primary',
        attrs: { type: 'button' },
        text: 'Ajouter',
        events: {
          click: async () => {
            if (!requireOnline()) return;
            try {
              const name = newAisleInput.value.trim();
              if (!name) {
                showToast('Le nom est requis', 3000, 'error');
                return;
              }
              const aisleId = await addAisle(storeId, name);
              const store = await getStore(storeId);
              const newAisle = await import('../db/aisles.js').then((m) => m.getAisle(aisleId));
              const pushRes = await pushCreateAisle(store?.serverId, name, newAisle?.order ?? 0);
              if (pushRes?.id) await setAisleServerId(aisleId, pushRes.id);
              newAisleInput.value = '';
              showToast('Rayon ajouté', 2000, 'success');
              refresh();
            } catch (err) {
              showToast(err.message || 'Erreur', 3000, 'error');
            }
          },
        },
      });

      page.appendChild(createElement('section', {
        className: 'page__section page-settings__aisles',
        children: [
          createElement('div', {
            className: 'page-settings__aisles-header',
            children: [
              createElement('h2', { className: 'page-settings__aisles-title', text: 'Rayons' }),
              createElement('span', {
                className: 'page-settings__aisles-count',
                text: `${aisles.length} rayon${aisles.length !== 1 ? 's' : ''}`,
              }),
            ],
          }),
          aisleList,
          createElement('div', {
            className: 'page-settings__add-form mt-md',
            children: [
              createElement('div', { className: 'input-group', children: [newAisleInput] }),
              addAisleBtn,
            ],
          }),
        ],
      }));

      newAisleInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') addAisleBtn.click();
      });
    }

    // Zone danger (owner uniquement)
    if (isOwner) {
      page.appendChild(createElement('section', {
        className: 'page-settings__danger-zone',
        children: [
          createElement('h3', { className: 'page-settings__danger-title', text: 'Zone de danger' }),
          createElement('button', {
            className: 'btn btn--danger btn--block',
            attrs: { type: 'button' },
            text: 'Supprimer ce magasin',
            events: { click: () => confirmDeleteStore(store.name, storeId) },
          }),
        ],
      }));
    }

    container.appendChild(page);
  } catch (err) {
    showToast(err.message || 'Erreur lors du chargement', 3000, 'error');
    navigate('/');
  }
}

function confirmDeleteStore(storeName, storeId) {
  openModal({
    title: 'Supprimer le magasin',
    content: createElement('p', {
      text: `Êtes-vous sûr de vouloir supprimer « ${storeName} » ? Cette action est irréversible.`,
    }),
    footer: createElement('div', {
      attrs: { style: 'display: flex; gap: var(--space-sm); width: 100%' },
      children: [
        createElement('button', {
          className: 'btn btn--ghost btn--block',
          attrs: { type: 'button' },
          text: 'Annuler',
          events: { click: closeModal },
        }),
        createElement('button', {
          className: 'btn btn--danger btn--block',
          attrs: { type: 'button' },
          text: 'Supprimer',
          events: {
            click: async () => {
              if (!requireOnline()) return;
              try {
                const storeToDelete = await getStore(storeId);
                const serverIdToDelete = storeToDelete?.serverId;
                await deleteStore(storeId);
                pushDeleteStore(serverIdToDelete);
                closeModal();
                showToast('Magasin supprimé', 2000, 'success');
                navigate('/');
              } catch (err) {
                showToast(err.message || 'Erreur', 3000, 'error');
              }
            },
          },
        }),
      ],
    }),
  });
}
